import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { TypeOf, z } from 'zod';

import { Card } from '@repo/ui/Card';
import { Form } from '@repo/ui/Form';
import { TextInputField } from '@repo/ui/Form';
import { Button } from '@repo/ui/Button';
import { registerInput, useZodForm } from '@repo/hooks/useZodForm';
import type { Mercury } from '@repo/types/Models';

interface UpdateUserFormProps {
  userId: Mercury.Models.User['id'];
  first_name: Mercury.Models.User['first_name'];
  last_name: Mercury.Models.User['last_name'];
  email: Mercury.Models.User['email'];
  refetch: () => void;
}

const updateUserFormSchema = z.object({
  first_name: z.string().min(2, { message: 'Name must contain at least 2 characters.' }),
  last_name: z.string().min(2, { message: 'Name must contain at least 2 characters.' }),
  email: z.string().email({ message: 'Please submit a valid email address.' }),
});
type UpdateUserFormParams = TypeOf<typeof updateUserFormSchema>;

const UpdateUserForm = ({ userId, email, first_name, last_name, refetch }: UpdateUserFormProps) => {
  // Mutation
  const UPDATE_USER = gql`
    mutation UpdateUser($id: ID!, $input: UpdateUserInput!) {
      updateUser(id: $id, input: $input) {
        user {
          id
          first_name
          last_name
          email
        }
      }
    }
  `;

  const client = useApolloClient();
  const onSubmit = async (input: UpdateUserFormParams) => {
    await client.mutate({
      mutation: UPDATE_USER,
      variables: {
        id: userId,
        input,
      },
    });

    refetch();
  };
  const form = useZodForm({
    schema: updateUserFormSchema,
    onSubmit,
  });

  return (
    <>
      <Card title="Update user basic information">
        <Form {...form}>
          <Form.Success>User successfully updated.</Form.Success>
          <Form.Error>
            Unable to update user. Please try again later or contact support if error persists.
          </Form.Error>
          <TextInputField
            label="First name"
            defaultValue={first_name}
            {...registerInput(form, 'first_name')}
          />
          <TextInputField
            label="Last name"
            defaultValue={last_name}
            {...registerInput(form, 'last_name')}
          />
          <TextInputField
            type="email"
            label="Email"
            defaultValue={email}
            {...registerInput(form, 'email')}
          />

          <div className="w-full">
            <Button type="submit">Update</Button>
          </div>
        </Form>
      </Card>
    </>
  );
};

export default UpdateUserForm;
